import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import { AnyAction, Dispatch } from 'redux'
import { Helmet } from 'react-helmet'
import copy from 'copy-to-clipboard'
import { AppState } from '../../../store'
import { UserController } from '../../../controllers'
import Notification from '../../../utilities/Notification'
import { setCurrentUser } from '../../../store/authentication/actions'
import { showTwoFactorAuthenticationModal } from '../../../store/modals/actions'
import { CurrentUser } from '../../../types'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TrashIcon } from 'lucide-react'

interface IDispatchToProps {
  setCurrentUser: typeof setCurrentUser
  showTwoFactorAuthenticationModal: typeof showTwoFactorAuthenticationModal
}

const Security = (props: IDispatchToProps) => {
  const { setCurrentUser, showTwoFactorAuthenticationModal } = props
  const { t } = useTranslation()
  const currentUser = useSelector<AppState, CurrentUser>(state => state.authentication.currentUser)
  const accessToken = useSelector<AppState, string>(state => state.authentication.accessToken)

  const onChangePasswordClick = (e: React.MouseEvent) => {
    e.preventDefault()

    window.location.href = `/passwords/new?email=${currentUser.email}`
  }

  const onToggleTwoFactorAuthentication = (e: React.MouseEvent) => {
    e.preventDefault()

    showTwoFactorAuthenticationModal({
      onSubmit: async () => {
        try {
          const user = await UserController.me()
          setCurrentUser(user)
        } catch (ex) {
          console.error(ex)
        }
      }
    })
  }

  const onDeleteAccountClick = (e: React.MouseEvent) => {
    e.preventDefault();

    const { email } = currentUser

    UserController
      .deleteUser()
      .then((response) => {
        if (response.success) {
          Notification.notifySuccess(t('ProfileData::A confirmation mail with instructions to delete your account has been sent to {{email}}', { email: email }));
        }
      })
  }

  const onCopyAccessToken = () => {
    copy(accessToken)
    Notification.notifySuccess(t('ProfileDetails::Copied to clipboard'))
  }

  return (
    <>
      <Helmet>
        <title>{t('ProfileSecurity::{{__appName}} | Security')}</title>
      </Helmet>

      <div className='flex flex-col gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('ProfileSecurity::Security')}</CardTitle>
            <CardDescription>{t('ProfileSecurity::Manage your password and two-factor authentication.')}</CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col divide-y divide-border'>
            <div className='flex flex-col items-start gap-3 pb-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex flex-col gap-1'>
                <p className='text-sm font-medium text-foreground'>{t('ProfileSecurity::Password')}</p>
                <p className='text-sm text-muted-foreground'>{t('ProfileSecurity::Change the password you use to sign in.')}</p>
              </div>
              <Button variant='outline' className='w-full sm:w-auto shrink-0' onClick={onChangePasswordClick}>{t('ProfileSecurity::Edit password')}</Button>
            </div>

            <div className='flex flex-col items-start gap-3 pt-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex flex-col gap-1'>
                <div className='flex items-center gap-2'>
                  <p className='text-sm font-medium text-foreground'>{t('ProfileSecurity::Two-factor authentication')}</p>
                  <Badge variant={currentUser.otp_enabled ? 'default' : 'outline'}>
                    {currentUser.otp_enabled ? t('ProfileSecurity::Enabled') : t('ProfileSecurity::Disabled')}
                  </Badge>
                </div>
                <p className='text-sm text-muted-foreground'>{t('ProfileSecurity::Add an extra layer of security to your account.')}</p>
              </div>
              <Button variant='outline' className='w-full sm:w-auto shrink-0' onClick={onToggleTwoFactorAuthentication}>
                {currentUser.otp_enabled ? t('ProfileSecurity::Disable two-factor authentication') : t('ProfileSecurity::Enable two-factor authentication')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('ProfileSecurity::API access')}</CardTitle>
            <CardDescription>{t('ProfileSecurity::Use this personal access token to authenticate API requests.')}</CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            <div className='min-w-0 flex-1'>
              <input readOnly value={accessToken} className='w-full font-mono' />
            </div>
            <Button variant='outline' className='w-full sm:w-auto shrink-0' onClick={onCopyAccessToken}>{t('ProfileDetails::Copy')}</Button>
          </CardContent>
        </Card>

        <Card className='border-destructive/30'>
          <CardHeader>
            <CardTitle className='text-destructive!'>{t('ProfileData::Account deletion')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription className='text-destructive'>{t('ProfileData::By deleting your account you will also delete all associated data from the account. Deleting your account is irreversible and all data will be lost.')}</AlertDescription>
            </Alert>
            <Button variant='destructive' className='w-full sm:w-auto shrink-0' onClick={onDeleteAccountClick}>
              <TrashIcon />
              {t('ProfileData::Delete account')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    setCurrentUser: (user: CurrentUser) => dispatch(setCurrentUser(user)),
    showTwoFactorAuthenticationModal: (options) => dispatch(showTwoFactorAuthenticationModal(options))
  }
}

export default connect(null, mapDispatchToProps)(Security)
